import { useEffect, useRef, useState } from "react";

const TICK_MS = 1000;
const DEFAULT_WARN_AT_REMAINING_MS = 5 * 60 * 1000;
const FINAL_MINUTE_MS = 60 * 1000;

function storageKey(sessionId: string) {
  return `a2-overall-timer-${sessionId}`;
}

function readStored(sessionId: string): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(storageKey(sessionId));
    if (!raw) return 0;
    const v = Number.parseInt(raw, 10);
    return Number.isFinite(v) && v >= 0 ? v : 0;
  } catch {
    return 0;
  }
}

function writeStored(sessionId: string, ms: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(sessionId), String(ms));
  } catch {
    // private mode / quota — accept loss across refresh.
  }
}

export type OverallTimerProps = {
  sessionId: string;
  totalMinutes: number;
  active: boolean;
  onExpire?: () => void;
  onOneMinuteWarning?: () => void;
  warnAtRemainingMs?: number;
  onWarnThresholdCrossed?: () => void;
  variant?: "ember" | "duo";
};

/**
 * Real-simulation total-exam timer for the full DUO oefenexamen.
 *
 * - Counts only "answering" time.
 * - Persists elapsed milliseconds to localStorage every tick.
 * - Goes negative past zero so overrun is visible (-01:23).
 */
export function OverallTimer({
  sessionId,
  totalMinutes,
  active,
  onExpire,
  onOneMinuteWarning,
  warnAtRemainingMs = DEFAULT_WARN_AT_REMAINING_MS,
  onWarnThresholdCrossed,
  variant = "ember",
}: OverallTimerProps) {
  const totalMs = totalMinutes * 60 * 1000;
  const [elapsedMs, setElapsedMs] = useState<number>(0);
  const expiredFiredRef = useRef(false);
  const oneMinuteFiredRef = useRef(false);
  const warnThresholdFiredRef = useRef(false);

  useEffect(() => {
    setElapsedMs(readStored(sessionId));
  }, [sessionId]);

  useEffect(() => {
    if (!active) return;
    const handle = setInterval(() => {
      setElapsedMs((prev) => {
        const next = prev + TICK_MS;
        writeStored(sessionId, next);
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(handle);
  }, [active, sessionId]);

  const remainingMs = totalMs - elapsedMs;
  const overtime = remainingMs < 0;
  const absSeconds = Math.floor(Math.abs(remainingMs) / 1000);
  const mm = Math.floor(absSeconds / 60).toString().padStart(2, "0");
  const ss = (absSeconds % 60).toString().padStart(2, "0");
  const display = overtime ? `−${mm}:${ss}` : `${mm}:${ss}`;

  useEffect(() => {
    if (
      !warnThresholdFiredRef.current &&
      remainingMs <= warnAtRemainingMs &&
      remainingMs > 0
    ) {
      warnThresholdFiredRef.current = true;
      onWarnThresholdCrossed?.();
    }
    if (!oneMinuteFiredRef.current && remainingMs <= FINAL_MINUTE_MS && remainingMs > 0) {
      oneMinuteFiredRef.current = true;
      onOneMinuteWarning?.();
    }
    if (!expiredFiredRef.current && remainingMs <= 0) {
      expiredFiredRef.current = true;
      onExpire?.();
    }
  }, [remainingMs, onExpire, onOneMinuteWarning, onWarnThresholdCrossed, warnAtRemainingMs]);

  let color = "var(--ink-muted)";
  if (overtime) color = "var(--fail)";
  else if (remainingMs <= warnAtRemainingMs) color = "var(--warn)";

  const ariaLabel = overtime
    ? `Examenmodus: ${absSeconds} seconden over de tijd${active ? "" : ", gepauzeerd"}.`
    : `Examenmodus: ${absSeconds} seconden over${active ? "" : ", gepauzeerd"}.`;

  if (variant === "duo") {
    let duoColor: string | undefined;
    if (overtime) duoColor = "#fda4af";
    else if (remainingMs <= warnAtRemainingMs) duoColor = "#fde68a";
    const totalMinutesRemaining = Math.max(0, Math.floor(remainingMs / 60_000));
    const duoText = overtime
      ? `−${mm}:${ss}`
      : remainingMs < 60_000
        ? `0:${ss}`
        : `${totalMinutesRemaining} min`;
    return (
      <span
        role="timer"
        aria-live="off"
        aria-label={ariaLabel}
        className="inline-flex items-baseline tabular-nums text-[14px] font-medium leading-none"
        style={duoColor ? { color: duoColor, transition: "color 200ms ease" } : undefined}
      >
        {duoText}
      </span>
    );
  }

  return (
    <div
      role="timer"
      aria-live="off"
      aria-label={ariaLabel}
      className="flex items-baseline gap-1.5 leading-none"
    >
      <span className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)]">
        Examen
      </span>
      <span
        className="font-mono-badge tabular-nums text-sm"
        style={{ color, transition: "color 200ms ease" }}
        aria-hidden="true"
      >
        {display}
      </span>
      {!active ? (
        <span
          aria-hidden="true"
          className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)]"
        >
          · pauze
        </span>
      ) : null}
    </div>
  );
}
